import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { getLocale, history } from '@umijs/max';
import { message, notification } from 'antd';
import { getToken, removeToken } from '@/utils/auth';
import zhCN from '@/locales/zh_CN';
import enUS from '@/locales/en_US';
import kmKH from '@/locales/km_KH';

const messagesByLocale: Record<string, Record<string, string>> = {
  zh_CN: zhCN,
  en_US: enUS,
  km_KH: kmKH,
};

function getI18nMessage(id: string): string {
  const locale = getLocale?.();
  const key = locale === 'zh-CN' ? 'zh_CN' : locale === 'en-US' ? 'en_US' : 'km_KH';
  const messages = messagesByLocale[key] ?? zhCN;
  return messages[id] ?? id;
}

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
  code?: number;
  message?: string;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 响应适配器：将后端响应格式适配为 ProComponents 需要的格式
    // @ts-expect-error - adaptor 在 Umi 4 中可能类型定义不完整，但功能可用
    adaptor: (resData: any) => {
      return {
        ...resData,
        success: resData.code === 0,
        errorMessage: resData.message,
      };
    },
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType, code } =
        res as unknown as ResponseStructure;
      // 处理业务错误（code !== 0 或 success === false）
      if (success === false || (code !== undefined && code !== 0)) {
        const error: any = new Error(errorMessage || getI18nMessage('错误.请求失败'));
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data, code };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      /** 优先使用 body 中的 message，否则用通用文案 */
      const getToastMessage = (body: any, fallback: string): string => {
        const msg = body?.message;
        if (typeof msg === 'string' && msg.trim()) return msg.trim();
        return fallback;
      };

      // 处理 401 未授权错误（Token 失效）
      if (error.response?.status === 401 || error.info?.code === 1002) {
        removeToken();
        const { pathname } = history.location;
        if (pathname !== '/user/login') {
          const toastMsg = getToastMessage(
            error.response?.data,
            getI18nMessage('错误.登录已过期'),
          );
          message.error(toastMsg);
          history.push('/user/login');
        }
        return;
      }

      // 我们的 errorThrower 抛出的错误（业务 code !== 0，此时 errorMessage 来自 adaptor 的 resData.message）
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          const displayMessage = errorMessage || getI18nMessage('错误.请求失败');
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(displayMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(displayMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: displayMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(displayMessage);
          }
        }
      } else if (error.response) {
        // Axios 错误：服务器返回了非 2xx 状态码，优先用 body.message，否则通用提示
        const body = error.response?.data;
        const statusPart = error.response?.status ? ` (${error.response.status})` : '';
        const fallback = getI18nMessage('错误.请求失败') + statusPart;
        message.error(getToastMessage(body, fallback));
      } else if (error.request) {
        // 请求已发出但未收到响应（无 body）
        message.error(getI18nMessage('错误.网络错误'));
      } else {
        // 发起请求时出错（无 body）
        message.error(getI18nMessage('错误.请求错误'));
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 添加 Bearer Token 认证头
      const token = getToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      // 处理业务错误码（code !== 0）
      if (data?.code !== undefined && data.code !== 0) {
        // 错误会在 errorThrower 中处理
        return response;
      }

      return response;
    },
  ],
};
